import interceptor from '../common/interceptor';
import { sentenceCase } from '../common/helper';
import { APIPaths } from '../common/constants';
import {
  AdditionalRecipientOption,
  AlertConfigData,
  ApiResponse,
  FeaturePermissionMatrix,
  LocationData,
  LocationTypeData,
  OperatingHours,
  PaginatedApiResponse,
  TeamData,
  TeamWithUsers,
} from '@/apis/types';
import {
  ApiActionResponse,
  ApiAgentsListResponse,
  ApiListResponse,
  CreateAgentPayload,
  TestAgentPayload,
  TestEmailAgentPayload,
} from '@/pages/Settings/AiAgents/types';
import { AiTypesConfigData } from '@/pages/Settings/AiControls/types';
import type {
  GxPEvaluationSettingsData,
  GxPEvaluationSettingsPayload,
  GxPReportsListPayload,
  GxPReportsListResponse,
} from '@/pages/Settings/GxPReports/components/types';
import { OperationType } from '@/common/enum';

//<!-------On Bording Steps-------->

//Profile

export const SubmitSettingsProfile = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.settingsProfile, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};
export const transferOwnership = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ownershipTransfer, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export interface ProfileTeam {
  name?: string;
  role_name?: string;
}

export interface ProfileData {
  name?: string;
  email?: string;
  phone_number?: string;
  teams?: ProfileTeam[];
}

type GetSettingsProfileResponse = ApiResponse<ProfileData>;

export const GetSettingsProfile = (
  token: string
): Promise<GetSettingsProfileResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.settingsProfile, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

//sensors
export interface CredentialDescription {
  title: string;
  href?: string;
  isLink?: boolean;
  linkText?: string;
}

export interface SensorData {
  id: string;
  company: string;
  sensor_id: string;
  /** Catalog provider key (e.g. `zlsim_sensor` for the ZoomLogi Simulator). */
  provider_key?: string | null;
  name?: string;
  sensor_name: string;
  client_id: string;
  client_secret: string;
  credential: Record<string, string>;
  credentials?: Array<{
    id?: string;
    name?: string;
    credential_name?: string;
    description: string;
    is_primary: boolean;
    order_index?: number;
    is_update?: boolean;
    is_delete?: boolean;
    credential?: Record<string, string>;
    credentials?: Record<string, string>;
  }>;
  status: string;
  description: string;
  sensor_description?: string;
  is_all_teams?: boolean;
  total_credentials?: number;
  credential_description: CredentialDescription[];
  teams: TeamData[];
  /** Mirror of parsed teams for labels when `teams` is filtered. */
  teams_detail?: TeamData[];
  required_inputs: string[];
  permissions: FeaturePermissionMatrix;
}

type GetCompanySensorsResponse = PaginatedApiResponse<SensorData[]>;
type GetCompanySensorDetailResponse = ApiResponse<SensorData>;

export const GetCompanySensors = (
  token: string,
  page?: number,
  search?: string
): Promise<GetCompanySensorsResponse> => {
  const params = new URLSearchParams();

  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.linkSensorsAccount}?${params.toString()}`,
        token,
        null
      )
      .then(resolve)
      .catch((error) => {
        console.error('Error fetching sensors:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetSettingsCompanySensors = (
  token: string,
  page?: number,
  search?: string
): Promise<GetCompanySensorsResponse> => {
  const params = new URLSearchParams();

  if (page != null) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.linkSensorsAccountV2}?${params.toString()}`,
        token,
        null
      )
      .then(resolve)
      .catch((error) => {
        console.error('Error fetching settings sensors:', error?.response);
        reject(error?.response);
      });
  });
};

export const GetCompanySensorDetail = (
  token: string,
  id: string
): Promise<GetCompanySensorDetailResponse> => {
  const url = `${APIPaths.linkSensorsAccountV2}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        console.error('Error fetching sensor detail:', error?.response);
        reject(error?.response);
      });
  });
};

export const DeleteCompanySensor = (
  id: string,
  token: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.linkSensorsAccount}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const DeleteSettingsCompanySensor = (
  id: string,
  token: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.linkSensorsAccountV2}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

//locations

interface GetSettingsLocationsData {
  data: LocationData[];
}

type GetSettingsLocationsResponse = ApiResponse<GetSettingsLocationsData>;

export const GetSettingsLocations = (
  token: string,
  dropdown?: boolean
): Promise<GetSettingsLocationsResponse> => {
  return new Promise((resolve, reject) => {
    let url = APIPaths.settingsLocation;
    if (dropdown) {
      url = `${APIPaths.settingsLocation}?dropdown=true`;
    }
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Full-detail single-location fetch — the dropdown-mode list omits polygon/
// lat/lng, so a caller that matched a location from that list needs this to
// get the fields dropdown mode doesn't return.
export const GetSettingsLocationById = (
  token: string,
  id: string
): Promise<ApiResponse<LocationData>> => {
  const url = `${APIPaths.settingsLocation}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const AddSettingsLocation = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.settingsLocation, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const AddLocationType = (model: { name: string }): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.locationTypes, model)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetLocationsType = (
  token: string
): Promise<ApiResponse<LocationTypeData[]>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.locationTypes, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetSettingsCouriers = (token: string): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.transportProvider, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// fetch company couriers for new settings screen.
interface GetSettingsNewCouriersData {
  couriers?: unknown[];
}

type GetSettingsNewCouriersResponse = ApiResponse<GetSettingsNewCouriersData>;

export const GetSettingsNewCouriers = (
  token: string,
  page = 1,
  search = ''
): Promise<GetSettingsNewCouriersResponse> => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.transportProviderDetail}?${params.toString()}`,
        token,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// fetch company couriers for home screen with service level.
export const GetHomeServiceLevelCouriers = (
  token: string,
  page = 1,
  search = ''
): Promise<unknown> => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.service_level_transport_provider}?${params.toString()}`,
        token,
        null
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

//get settings courier by id
export const GetSettingsNewCouriersById = (
  id: string,
  token: string
): Promise<unknown> => {
  const url = `${APIPaths.transportProviderDetail}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const LinkSettingsCourierAccount = (
  token: string,
  model: unknown
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.courierContacts, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Create Teams List
export interface CreateTeamModel {
  color: string;
  name: string;
  general_support_email?: string;
  slack_webhook?: string;
  teams_webhook?: string;
}

export const CreateTeam = (
  token: string,
  model: CreateTeamModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.teams, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Get Teams List
export interface GetTeamsResponse {
  data: {
    data: {
      message: string;
      status: number;
      is_success: boolean;
      messageTypeId: string;
      data: CompanyTeam[];
      page_count: number;
      total_count: number;
    };
    message: {
      text: string;
      title: string;
      messageTypeId: string;
    };
  };
  status: number;
  statusText: string;
}

export interface CompanyTeam {
  id: string;
  name: string;
  color: string;
  general_support_email?: string | null;
  slack_webhook?: string | null;
  slack_connected?: boolean;
  teams_webhook?: string | null;
  company: string;
  is_connected_with_anything: boolean;
  transport_providers: number;
  locations: number;
  shipments: number;
  products: number;
  sensors: number;
  users: number;
  notifications: number;
  permissions: FeaturePermissionMatrix;
}

export const GetTeams = (token: string): Promise<GetTeamsResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.teams, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

//Get Teams List with search and pagination

type GetTeamsWithSearchAndPaginationResponse = PaginatedApiResponse<
  CompanyTeam[]
>;

export const GetTeamsWithSearchAndPagination = (
  token: string,
  search = '',
  page = 1
): Promise<GetTeamsWithSearchAndPaginationResponse> => {
  let url = `${APIPaths.teams}?page=${page}`;
  if (search) {
    url = `${APIPaths.teams}?search=${search}&page=${page}`;
  }
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

//Update Team
interface UpdateTeamModel {
  id: string;
  color: string;
  name: string;
  general_support_email?: string;
  slack_webhook?: string;
  teams_webhook?: string;
}

export const UpdateTeam = (
  token: string,
  model: UpdateTeamModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut('Post', `${APIPaths.teams}`, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Validate/connect a team webhook (Slack or Teams). If team_id is provided,
// backend will persist on success; otherwise it only validates.
interface ConnectWebhookModel {
  team_id?: string;
  provider: 'slack' | 'teams';
  webhook_url: string;
}

export const ConnectTeamWebhook = (
  token: string,
  model: ConnectWebhookModel
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.team_connect_webhook, model, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

// Delete Team
export const DeleteTeam = (token: string, id: string): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', `${APIPaths.teams}${id}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// locations
export const DeleteLocation = (
  token: string,
  id: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.settingsLocation}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', url, null, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Partial update clearing just the polygon — LocationV2Serializer.update()
// only touches the fields present in the payload, so this leaves the rest
// of the location (contacts included) untouched.
export const ClearLocationPolygon = (
  token: string,
  id: string
): Promise<ApiResponse> => {
  const url = `${APIPaths.settingsLocation}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Patch', url, { polygon: [] }, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

/** Saves only a location's operating hours (partial PATCH). */
export const UpdateLocationOperatingHours = (
  token: string,
  id: string,
  operatingHours: OperatingHours | null
): Promise<ApiResponse<LocationData>> => {
  const url = `${APIPaths.settingsLocation}${id}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Patch', url, { operating_hours: operatingHours }, token)
      .then((res) => {
        resolve(res as ApiResponse<LocationData>);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

const TIMEZONE_LOOKUP_TIMEOUT_MS = 3000;

/** Time zone for an address's coordinates, before the location is saved. */
export const GetTimezoneForCoordinates = (
  token: string,
  lat: number,
  lng: number
): Promise<ApiResponse<{ timezone: string }>> => {
  const params = new URLSearchParams({ lat: String(lat), lng: String(lng) });
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.settingsLocation}timezone/?${params.toString()}`,
        token,
        null,
        null,
        // Best effort: no error toast; the zone is still detected on save.
        true,
        // The form holds legacy hours' Edit until this settles, so cap each
        // attempt (the lookup itself takes milliseconds).
        TIMEZONE_LOOKUP_TIMEOUT_MS
      )
      .then((res) => {
        resolve(res as ApiResponse<{ timezone: string }>);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

//contacts

export interface TeamsUsersResponseData {
  teams_users: TeamWithUsers[];
  additional: AdditionalRecipientOption[];
}

export const GetUsersByTeamIds = (
  token: string,
  teamIds: string[] = []
): Promise<ApiResponse<TeamsUsersResponseData>> => {
  const teamsParam = teamIds.join(',');
  let url = APIPaths.teamUsers;
  if (teamsParam) {
    url = `${APIPaths.teamUsers}?teams=${teamsParam}`;
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// get all pending user [mahtab]

export const GetAllPendingUsers = (
  token: string,
  isActive: string | null = null
): Promise<unknown> => {
  const params = new URLSearchParams();

  if (isActive === null) {
    params.append('is_pending', 'true');
  } else {
    params.append('is_active', isActive);
  }

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.users}?${params.toString()}`, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Delete Pending Invite User
export const DeletePendingInviteUser = (
  token: string,
  id: string
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', `${APIPaths.users}${id}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Resend Invite
export const ResendInvite = (
  token: string,
  model: unknown
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.resendInvite, model, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export interface BulkInviteTeam {
  team: string;
  role: string;
  team_name?: string;
  role_name?: string;
  color?: string;
}

export interface BulkInviteUserRow {
  row: number;
  name: string;
  title: string;
  email: string;
  phone_number: string;
  /** Defaults to true when omitted from the spreadsheet. */
  send_email?: boolean;
  teams: BulkInviteTeam[];
  errors: Record<string, string>;
  key?: string;
}

export interface BulkParseResult {
  users: BulkInviteUserRow[];
  valid_count: number;
  error_count: number;
}

export const downloadInviteTemplate = (
  token: string
): Promise<{ blob: Blob; filename: string }> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGetDownload('Get', APIPaths.userInviteTemplate, token)
      .then((res) => {
        const disposition = res.headers?.['content-disposition'] || '';
        const match = /filename="?([^"]+)"?/i.exec(disposition);
        resolve({
          blob: res.data as Blob,
          filename: match?.[1] || 'user_invite_template.csv',
        });
      })
      .catch((error) => {
        reject(error?.response || error);
      });
  });
};

export const parseBulkInviteUsers = (
  token: string,
  formData: FormData
): Promise<ApiResponse<BulkParseResult>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload('Post', APIPaths.userBulkParse, formData, token)
      .then((res) => resolve(res as ApiResponse<BulkParseResult>))
      .catch((error) => {
        reject(error?.response || error);
      });
  });
};

interface AiFlowToggleData {
  access: string;
  refresh?: string;
}

export const AiFlowToggle = (
  token: string,
  model: unknown
): Promise<ApiResponse<AiFlowToggleData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.aiToggle, model, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

interface GetAiTransferNumberData {
  ai_transfer_number: string | null;
}

export const GetAiTransferNumber = (
  token: string
): Promise<ApiResponse<GetAiTransferNumberData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.aiTransferNumber, token, null)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

interface GetAiSettingsData {
  ai_enabl: boolean;
  ai_transfer_number: string | null;
  ai_agent_number: string | null;
  ai_controls?: AiTypesConfigData[];
}

export const GetAiSettings = (
  token: string
): Promise<ApiResponse<GetAiSettingsData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.aiSettings, token, null)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

interface UpdateAiSettingsPayload {
  ai_enabl?: boolean | string;
  ai_transfer_number?: string;
  refresh?: string;
}

interface UpdateAiSettingsData {
  ai_enabl: boolean;
  ai_transfer_number: string | null;
  ai_agent_number: string | null;
  access: string;
  refresh: string;
}

export const UpdateAiSettings = (
  token: string,
  model: UpdateAiSettingsPayload
): Promise<ApiResponse<UpdateAiSettingsData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.aiSettings, model, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const UpdateAiControlsSettings = (
  model: UpdateAiSettingsPayload,
  token: string
): Promise<ApiResponse<UpdateAiSettingsData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.aiSettings, model, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export interface AlertThresholdsData {
  setup_sensor?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export const GetAlertThresholds = (
  token: string,
  query = ''
): Promise<ApiResponse<AlertThresholdsData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.alertThreshold}${query}`, token)
      .then((res) => resolve(res))
      .catch((err) => reject(err?.response));
  });
};

// get alert customization
export interface AlertThresholdItem {
  alerts: AlertConfigData[];
  id?: string;
  name?: string;
  phase_display: string;
}

interface GetAlertsCustomizationByShipmentIdResponse {
  data: {
    data: {
      is_success: boolean;
      data: AlertThresholdItem[];
    };
  };
}

export const GetAlertsCustomizationByShipmentId = (
  token: string,
  teamId?: string
): Promise<GetAlertsCustomizationByShipmentIdResponse> => {
  let url = `${APIPaths.alertCustomization}by_phase/`;
  if (teamId) {
    url = `${APIPaths.alertCustomization}by_phase/?team=${teamId}`;
  }
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res: unknown) => {
        const response = res as GetAlertsCustomizationByShipmentIdResponse;
        const list = response?.data?.data?.data;
        if (Array.isArray(list)) {
          response.data.data.data = list.map((group: AlertThresholdItem) => ({
            ...group,
            alerts: Array.isArray(group.alerts)
              ? group.alerts.map((alert: AlertConfigData) => ({
                  ...alert,
                  alert_name: alert.alert_name
                    ? sentenceCase(alert.alert_name)
                    : alert.alert_name,
                }))
              : group.alerts,
          }));
        }
        resolve(response);
      })
      .catch((err) => reject(err?.response));
  });
};
// get alert customization

export interface AlertGroup {
  category?: string;
  icon?: string;
  phase: string;
  phase_display: string;
  alerts: AlertConfigData[];
}

type GetAlertsCustomizationResponse = ApiResponse<AlertGroup>;

export const GetAlertsCustomization = (
  token: string,
  query: string = ''
): Promise<GetAlertsCustomizationByShipmentIdResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.alertCustomization}by_phase/${query}`, token)
      .then((res: unknown) => {
        const response = res as GetAlertsCustomizationByShipmentIdResponse;
        const list = response?.data?.data?.data;
        if (Array.isArray(list)) {
          response.data.data.data = list.map((group: AlertThresholdItem) => ({
            ...group,
            alerts: Array.isArray(group.alerts)
              ? group.alerts.map((alert: AlertConfigData) => ({
                  ...alert,
                  alert_name: alert.alert_name
                    ? sentenceCase(alert.alert_name)
                    : alert.alert_name,
                }))
              : group.alerts,
          }));
        }
        resolve(response);
      })
      .catch((err) => reject(err?.response));
  });
};

export const GetAlertsAvailableActions = (
  token: string
): Promise<GetAlertsCustomizationResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.alertCustomization}available_actions/`,
        token
      )
      .then((res) => resolve(res))
      .catch((err) => reject(err?.response));
  });
};

// API call for copy alet teams settings [mahtab]
interface CopyTeamsParams {
  source_team_id?: string;
  destination_team_id?: string;
}

export const copyTeamsSettings = (
  token: string,
  model: CopyTeamsParams
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.alertCustomization}duplicate_team_configuration/`,
        model,
        token
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

type ResetAlertToDefaultResponse = ApiResponse<AlertConfigData>;

interface ResetAlertToDefaultPayload {
  team: string;
  all_team: boolean;
  alert_type: string;
}

export const ResetAlertToDefault = (
  model: ResetAlertToDefaultPayload,
  token: string
): Promise<ResetAlertToDefaultResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.alertCustomization}reset_to_default/`,
        model,
        token
      )
      .then((res) => resolve(res))
      .catch((err) => reject(err?.response));
  });
};

type UpdateSingleAlertResponse = ApiResponse<AlertConfigData>;

export interface UpdateSingleAlertPayload {
  team: string;
  all_team: boolean;
  alert_type: string;
  is_enabled: boolean;
  threshold_value?: string | number;
  threshold_value_2?: string | number | null;
  advanced_settings?: {
    departure_grace_planned_mins?: number;
    arrival_grace_planned_mins?: number;
    stop_check_actual_mins?: number;
    stop_check_planned_mins?: number;
    gps_accuracy_padding?: number;
    pre_collection_skip_count?: number;
    lower_bounds_degree_padding?: number;
    upper_bounds_degree_padding?: number;
    rolling_average_hours?: number;
    rate_of_change_factor?: number;
    sensor_data_start_delay_minutes?: number;
  };
  recommended_contacts?: string[];
  recommended_action?: string;
  recommended_action_type?: string;
  preferred_actions?: { action_type?: string; priority: number }[];
}

export const UpdateSingleAlert = (
  payload: UpdateSingleAlertPayload,
  token: string
): Promise<UpdateSingleAlertResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        `${APIPaths.alertCustomization}update_single_threshold/`,
        payload,
        token
      )
      .then((res) => resolve(res))
      .catch((err) => reject(err?.response));
  });
};

export interface SaveAlertThresholdsPayload {
  reset: boolean;
  all_team?: boolean;
  team?: string | string[];
  [key: string]: unknown;
}

export const SaveAlertThresholds = (
  token: string,
  payload: SaveAlertThresholdsPayload
): Promise<ApiResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.alertThreshold, payload, token)
      .then((res) => resolve(res))
      .catch((err) => reject(err?.response));
  });
};

interface CompanyInfoData {
  name?: string;
  ai_company_name?: string;
  ai_agent_number?: string;
  report_address_update_enabled?: boolean;
  report_delivery_time_enabled?: boolean;
  report_address_update_message?: string;
  report_delivery_time_message?: string;
  report_ask_address_change_scope?: boolean;
  show_emissions_estimate?: boolean;
  manual_shipment_track_enabled?: boolean;
  manual_shipment_quote_enabled?: boolean;
  manual_shipment_book_enabled?: boolean;
  manual_shipment_create_enabled?: boolean;
  alert_settings?: {
    version: number;
    owner_assignment_enabled: boolean;
    assignment_tags: Array<{ id: string; label: string; position: number }>;
  };
}

export const SubmitCompnayInfo = (
  token: string,
  model: unknown
): Promise<ApiResponse<CompanyInfoData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.companyInfo, model, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetCompnayInfo = (
  token: string
): Promise<ApiResponse<CompanyInfoData>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.companyInfo, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export interface CompanyPhoneNumberOption {
  phone_number: string;
  label: string | null;
  is_primary: boolean;
}

export const GetCompanyPhoneNumbers = (
  token: string,
  agentType?: string,
  currentAgentId?: string
): Promise<ApiResponse<CompanyPhoneNumberOption[]>> => {
  const params = new URLSearchParams();
  if (agentType) params.set('agent_type', agentType);
  if (currentAgentId) params.set('current_agent_id', currentAgentId);
  const query = params.size ? `?${params.toString()}` : '';
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.companyPhoneNumbers}${query}`, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetCourierContactsDirectory = (
  token: string,
  id: string | number
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.contactsDirectory}?courier=${id}`, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

interface ApiChangelogEntry {
  user_info?: {
    full_name?: string;
    title?: string;
  } | null;
  formatted_timestamp?: string | null;
}

interface GetCourierChangelogResponse {
  data?: {
    results?: ApiChangelogEntry[];
  };
}

export const GetSingleLocationType = (
  id: string,
  token: string
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', `${APIPaths.locationTypes}${id}/`, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const DeleteLocationType = (
  id: string,
  token: string
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosDelete('Delete', `${APIPaths.locationTypes}${id}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetCourierChangelog = (
  token: string,
  id: string | number
): Promise<GetCourierChangelogResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.courierChangelog}?company_courier=${id}`,
        token
      )
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

const withV2 = (url: string) =>
  url.includes('?') ? `${url}&version=v2` : `${url}?version=v2`;

export const GetAgentsWithPaginationAndSearch = (
  token: string,
  search = '',
  page = 1
): Promise<ApiAgentsListResponse> => {
  let url = `${APIPaths.ai_agents_list}?page=${page}`;

  if (search) {
    url = `${APIPaths.ai_agents_list}?search=${encodeURIComponent(search)}&page=${page}`;
  }

  url = withV2(url);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res: ApiAgentsListResponse) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const CreateAgent = (
  token: string,
  body: CreateAgentPayload
): ApiActionResponse => {
  const url = withV2(APIPaths.ai_agents_list);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', url, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetAgentById = (token: string, id: string): ApiActionResponse => {
  const url = withV2(`${APIPaths.ai_agents_list}${id}/`);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const UpdateAgent = (
  token: string,
  id: string,
  body: CreateAgentPayload
): ApiActionResponse => {
  const url = withV2(`${APIPaths.ai_agents_list}${id}/`);

  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut('Put', url, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const DeleteAgent = (id: string, token: string): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Delete', `${APIPaths.ai_agents_list}${id}/`, null, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const DeleteCustomCollectVar = (
  id: string,
  token: string
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Delete',
        `${APIPaths.ai_agent_custom_collect}${id}/`,
        null,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export type CreateCustomCollectVarPayload = {
  display_name: string;
  type: string;
  description: string;
  sample_value?: string;
};

export type CreateCustomCollectVarResult = {
  id: string;
  display_name: string;
  // Derived server-side from display_name, and read-only.
  name: string;
  type: string;
  description?: string;
  sample_value?: string;
};

export const CreateCustomCollectVar = (
  body: CreateCustomCollectVarPayload,
  token: string
): ApiActionResponse<CreateCustomCollectVarResult> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ai_agent_custom_collect, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export type UpdateCustomCollectVarPayload = {
  display_name?: string;
  type?: string;
  description?: string;
  sample_value?: string;
};

export const UpdateCustomCollectVar = (
  id: string,
  body: UpdateCustomCollectVarPayload,
  token: string
): ApiActionResponse<CreateCustomCollectVarResult> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPut('Put', `${APIPaths.ai_agent_custom_collect}${id}/`, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export type CustomCollectVarUsageWorkflow = {
  id: string;
  name: string;
};

export type CustomCollectVarUsageResult = {
  workflows: CustomCollectVarUsageWorkflow[];
};

export const GetCustomCollectVarUsage = (
  id: string,
  token: string
): ApiActionResponse<CustomCollectVarUsageResult> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet(
        'Get',
        `${APIPaths.ai_agent_custom_collect}${id}/usage/`,
        token,
        null
      )
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const TestAgent = (
  token: string,
  body: TestAgentPayload
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ai_agent_test, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const TestEmailAgent = (
  token: string,
  body: TestEmailAgentPayload
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ai_agent_test_email, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const ImproveAgentPrompt = (
  token: string,
  body: {
    behavior_prompt: string;
    collect_variables: string[];
    introduction?: string;
    prompt_edited?: boolean;
    variables_edited?: boolean;
    is_inbound?: boolean;
  }
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ai_agent_improve_prompt, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const ImproveResolutionPrompt = (
  token: string,
  body: {
    resolution_text: string;
    behavior_prompt: string;
  }
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.ai_agent_improve_resolution_prompt,
        body,
        token
      )
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const ValidateAgentPrompt = (
  token: string,
  body: { behavior_prompt: string; collect_variables: string[] }
): ApiActionResponse => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ai_agent_validate_prompt, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetAiAgentVoicesWithSearchAndPagination = (
  token: string,
  search = '',
  page = 1
) => {
  let url = `${APIPaths.ai_agent_voice}?page=${page}`;

  if (search) {
    url = `${APIPaths.ai_agent_voice}?search=${search}&page=${page}`;
  }

  return interceptor.axiosGet('Get', url, token, null);
};
export const GetAiAgentDropdownsOptions = (
  token: string
): ApiListResponse<{ value: string; label: string }> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Get',
        APIPaths.ai_agent_dropdowns,
        {},
        token,
        null,
        OperationType.Read
      )
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetAiAgentDynamicVariables = (
  token: string
): ApiListResponse<{ value: string; label: string }> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Get',
        APIPaths.ai_agent_dynamicVars,
        {},
        token,
        null,
        OperationType.Read
      )
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};
export const PostAiAgentDynamicVariablesUsed = (
  token: string,
  variables: string[]
) => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.ai_agent_dynamicVars, { variables }, token)
      .then((res) => resolve(res))
      .catch((error) => reject(error?.response));
  });
};

export const GetAgentTemplates = (
  token: string,
  type?: string
): ApiActionResponse => {
  const url = type
    ? `${APIPaths.ai_agent_template}?type=${type}`
    : APIPaths.ai_agent_template;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetTestAgentResult = (
  token: string,
  workflowEventUuid: string
): ApiActionResponse => {
  const url = `${APIPaths.ai_agent_test_result}${workflowEventUuid}/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetGxPSettings = (
  token: string
): ApiActionResponse<GxPEvaluationSettingsData> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.gxp_settings, token, null)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const SaveGxPSettings = (
  token: string,
  body: GxPEvaluationSettingsPayload
): ApiActionResponse<GxPEvaluationSettingsData> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Patch', APIPaths.gxp_settings, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};

// Time out of range (Temperature GxP) — per-product MKT/activation-energy/rule
// document settings. Backed by gxp/views/product_time_out_of_range
export interface TimeOutOfRangeDocument {
  id: string;
  name: string;
  created_by?: string;
  created_at?: string;
  url?: string;
  set_by?: string;
  set_at?: string;
}

export interface TimeOutOfRangeData {
  is_enabled: boolean;
  mkt_limit_celsius?: number | null;
  activation_energy_kj_mol?: number | null;
  mkt_must_not_exceed?: number | null;
  activation_energy?: number | null;
  label_range_min?: number | null;
  label_range_max?: number | null;
  allowable_range_above_min: number;
  allowable_range_below_min: number;
  files?: TimeOutOfRangeDocument[];
  documents?: TimeOutOfRangeDocument[];
}

type GetTimeOutOfRangeResponse = ApiResponse<TimeOutOfRangeData>;

export const GetProductTimeOutOfRange = (
  token: string,
  productId: string
): Promise<GetTimeOutOfRangeResponse> => {
  const url = `${APIPaths.gxp_products}${productId}/time-out-of-range/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', url, token, null)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const SaveProductTimeOutOfRange = (
  token: string,
  productId: string,
  formData: FormData
): Promise<GetTimeOutOfRangeResponse> => {
  const url = `${APIPaths.gxp_products}${productId}/time-out-of-range/`;
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPostForUpload('Patch', url, formData, token)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error?.response);
      });
  });
};

export const GetGxPReportsList = (
  token: string,
  body: GxPReportsListPayload
): Promise<GxPReportsListResponse> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.gxp_reports_list, body, token)
      .then((res) => resolve(res))
      .catch((error) => {
        reject(error?.response);
      });
  });
};
